import { DEFAULTS, FEED_SOURCES } from '../../constants';
import { toTrimmedString } from './shared';

const FEED_SOURCE_LABEL_BY_KEY = FEED_SOURCES.reduce((acc, { key, label }) => {
  if (key) acc[String(key).toLowerCase()] = label;
  if (label) acc[String(label).toLowerCase()] = label;
  return acc;
}, {});

const resolveFeedSourceLabel = (value) => {
  const normalized = toTrimmedString(value);
  if (!normalized) return '';
  return FEED_SOURCE_LABEL_BY_KEY[normalized.toLowerCase()] || normalized;
};

const toNumberOrZero = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const normalizeCurrencyRow = (currencyData) => {
  if (!currencyData || typeof currencyData !== 'object') return null;

  const fromCurrency = currencyData?.fromCurrency || '';
  const toCurrency = currencyData?.toCurrency || 'USD';

  const cash = toNumberOrZero(currencyData?.cash);
  const credit = toNumberOrZero(currencyData?.credit);
  const refund = toNumberOrZero(currencyData?.refund);
  const total = toNumberOrZero(currencyData?.total);

  const cashInUsd = toNumberOrZero(currencyData?.cashInUsd ?? currencyData?.cash_in_usd);
  const creditInUsd = toNumberOrZero(currencyData?.creditInUsd ?? currencyData?.credit_in_usd);
  const refundInUsd = toNumberOrZero(currencyData?.refundInUsd ?? currencyData?.refund_in_usd);
  const totalInUsd = toNumberOrZero(currencyData?.totalInUsd ?? currencyData?.total_in_usd);

  return {
    fromCurrency,
    toCurrency,
    cash,
    credit,
    refund,
    total,
    cashInUsd,
    creditInUsd,
    refundInUsd,
    totalInUsd,
  };
};

const normalizeCurrencies = (currencies) => {
  if (!Array.isArray(currencies) || currencies.length === 0) return [];

  return currencies.map((currencyData) => normalizeCurrencyRow(currencyData)).filter((currency) => currency !== null);
};

const normalizeFeedSourceRow = (row) => {
  if (!row || typeof row !== 'object') return null;

  const feedSource = resolveFeedSourceLabel(
    row?.feedSource ?? row?.feed_source ?? row?.source ?? row?.name ?? row?.key ?? row?.provider ?? row?.channel,
  );
  if (!feedSource) return null;

  const cash = toNumberOrZero(row?.cash ?? row?.totalCash ?? row?.total_cash);
  const credit = toNumberOrZero(row?.credit ?? row?.totalCredit ?? row?.total_credit);
  const refund = toNumberOrZero(row?.refund ?? row?.totalRefund ?? row?.total_refund);
  const parsedTotal = Number(row?.total);
  const total = Number.isFinite(parsedTotal) ? parsedTotal : cash + credit - refund;

  const currencies = normalizeCurrencies(row?.currencies || []);

  return { feedSource, cash, credit, refund, total, currencies };
};

const normalizeFeedSourceRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const normalizedBySource = new Map();
  rows.forEach((row) => {
    const normalized = normalizeFeedSourceRow(row);
    if (!normalized) return;

    if (!normalizedBySource.has(normalized.feedSource)) {
      normalizedBySource.set(normalized.feedSource, normalized);
      return;
    }

    const existing = normalizedBySource.get(normalized.feedSource);
    const mergedCash = existing.cash + normalized.cash;
    const mergedCredit = existing.credit + normalized.credit;
    const mergedRefund = existing.refund + normalized.refund;
    const mergedCurrencies = [...existing.currencies, ...normalized.currencies];

    normalizedBySource.set(normalized.feedSource, {
      feedSource: normalized.feedSource,
      cash: mergedCash,
      credit: mergedCredit,
      refund: mergedRefund,
      total: mergedCash + mergedCredit - mergedRefund,
      currencies: mergedCurrencies,
    });
  });

  return Array.from(normalizedBySource.values());
};

export const buildFeedSourceRowsFromSummary = (summary) => {
  if (!summary || typeof summary !== 'object') return [];

  const rows = [];
  const usedLabels = new Set();

  FEED_SOURCES.forEach(({ key, label }) => {
    const source = summary?.[key];
    if (!source || usedLabels.has(label)) return;

    const cash = Number(source?.cash) || 0;
    const credit = Number(source?.credit) || 0;
    const refund = Number(source?.refund) || 0;
    const currencies = normalizeCurrencies(source?.currencies || []);

    rows.push({
      feedSource: label,
      cash,
      credit,
      refund,
      total: cash + credit - refund,
      currencies,
    });
    usedLabels.add(label);
  });

  return rows;
};

export const extractFeedSourceData = (data) => {
  if (!data) return DEFAULTS.EMPTY_ARRAY;
  const payload = data?.data ?? data;
  const directList =
    payload?.feedSourceData ||
    payload?.feedSources ||
    payload?.summary ||
    payload?.summaries ||
    payload?.posting?.summaries ||
    payload?.feed_source_data ||
    payload?.feed_source_summary ||
    payload?.feedSourceSummary;

  if (Array.isArray(directList)) {
    const normalizedRows = normalizeFeedSourceRows(directList);
    return normalizedRows.length > 0 ? normalizedRows : DEFAULTS.EMPTY_ARRAY;
  }

  const summary =
    payload?.summary || payload?.feedSummary || payload?.feed_source_summary || payload?.feedSourceSummary || payload;
  const summaryRows = buildFeedSourceRowsFromSummary(summary);
  return summaryRows.length > 0 ? summaryRows : DEFAULTS.EMPTY_ARRAY;
};

export const extractSublocationBreakdown = (data) => {
  if (!data) return DEFAULTS.EMPTY_ARRAY;
  const payload = data?.data ?? data;

  // 1) Backward-compatible: old payload already provides FE-ready breakdown
  const directBreakdown = payload?.posting?.sublocationBreakdown || payload?.sublocationBreakdown || null;
  if (Array.isArray(directBreakdown) && directBreakdown.length > 0) return directBreakdown;

  // 2) New payload: summary/summaries rows contain nested sublocations for each source
  const summaryList = payload?.summary || payload?.summaries || payload?.posting?.summaries || null;
  if (!Array.isArray(summaryList) || summaryList.length === 0) return DEFAULTS.EMPTY_ARRAY;

  const bySublocationId = new Map();

  summaryList.forEach((sourceRow) => {
    const sourceLabel = resolveFeedSourceLabel(
      sourceRow?.source ?? sourceRow?.feedSource ?? sourceRow?.feed_source ?? sourceRow?.name,
    );
    if (!sourceLabel) return;

    const sublocations = Array.isArray(sourceRow?.sublocations) ? sourceRow.sublocations : [];
    sublocations.forEach((subloc) => {
      const sublocationId = Number(subloc?.sublocationId ?? subloc?.id);
      const safeKey = Number.isFinite(sublocationId) ? sublocationId : String(subloc?.sublocationName || '').trim();
      if (!safeKey) return;

      if (!bySublocationId.has(safeKey)) {
        bySublocationId.set(safeKey, {
          sublocationId: Number.isFinite(sublocationId) ? sublocationId : null,
          sublocationName: toTrimmedString(subloc?.sublocationName || subloc?.name || ''),
          feeds: {},
        });
      }

      const target = bySublocationId.get(safeKey);
      target.feeds[sourceLabel] = {
        cash: toNumberOrZero(subloc?.cash),
        credit: toNumberOrZero(subloc?.credit),
        refund: toNumberOrZero(subloc?.refund),
        total: toNumberOrZero(subloc?.total),
        cashInUsd: toNumberOrZero(subloc?.cashInUsd ?? subloc?.cash_in_usd ?? subloc?.cash),
        creditInUsd: toNumberOrZero(subloc?.creditInUsd ?? subloc?.credit_in_usd ?? subloc?.credit),
        refundInUsd: toNumberOrZero(subloc?.refundInUsd ?? subloc?.refund_in_usd ?? subloc?.refund),
        totalInUsd: toNumberOrZero(subloc?.totalInUsd ?? subloc?.total_in_usd ?? subloc?.total),
        fromCurrency: toTrimmedString(subloc?.fromCurrency || subloc?.from_currency || ''),
        toCurrency: toTrimmedString(subloc?.toCurrency || subloc?.to_currency || 'USD'),
      };
    });
  });

  return Array.from(bySublocationId.values());
};
